import dayjs from "dayjs";
import { useQuery } from "react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Descriptions, Row, Space, Typography } from "antd";

import { Card } from "misc/card";
import { Table } from "misc/tables";
import { LineChart } from "misc/charts";
import { StatusBoxWidget } from "misc/widgets/consumers";
import { DateField, DatetimeField, NumberField, TextField } from "misc/fields";
import { datalayerUrls } from "misc/urls";
import { stsColors } from "misc/styles/Colors";

const BmVinAssignments = ({ data = {}, isLoading, isError, isUpdating }) => {
    const columns = useMemo(
        () => [
            {
                dataIndex: "vin",
                title: <TextField value="t_vin" />,
                width: 200,
                render: value => <TextField value={value} />
            },
            {
                dataIndex: "batteryId",
                title: <TextField value="t_battery_id" />,
                width: 180,
                render: value => <TextField value={value} />
            },
            {
                dataIndex: "licensePlate",
                title: <TextField value="t_license_plate" />,
                width: 140,
                render: value => <TextField value={value} />
            },
            {
                dataIndex: "installedAt",
                title: <TextField value="t_battery_installation_date" />,
                width: 140,
                render: value => <DateField value={value} format="DD.MM.YYYY" />
            },
            {
                dataIndex: "removedAt",
                title: <TextField value="t_battery_removal_date" />,
                width: 140,
                render: value => <DateField value={value} format="DD.MM.YYYY" />
            },
            {
                dataIndex: "kmDriven",
                title: <TextField value="t_bm_battery_mileage" />,
                width: 160,
                render: value => <NumberField value={value} decimals={1} suffix="km" />
            },
            {
                dataIndex: "organisationalUnit",
                title: <TextField value="t_organisational_unit" />,
                width: 220,
                render: value => <TextField value={value} />
            }
        ],
        []
    );

    return (
        <Card title="t_battery_installations_history" isLoading={isLoading} isError={isError} isUpdating={isUpdating}>
            <Table
                dataSource={data?.data ?? []}
                columns={columns}
                size="small"
                pagination={false}
                expandedRowRender={record => (
                    <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label={<TextField value="t_bm_installation_hvr_number" />}>
                            <TextField value={record.installationHvrNumber} defaultValue="—" />
                        </Descriptions.Item>
                        <Descriptions.Item label={<TextField value="t_bm_installation_hvr_order_created_date" />}>
                            {record.installationHvrOrderCreatedDate != null ? (
                                <DateField value={record.installationHvrOrderCreatedDate} format="DD.MM.YYYY" />
                            ) : (
                                <TextField value={null} defaultValue="—" />
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label={<TextField value="t_bm_removal_hvr_number" />}>
                            <TextField value={record.removalHvrNumber} defaultValue="—" />
                        </Descriptions.Item>
                        <Descriptions.Item label={<TextField value="t_bm_removal_hvr_order_created_date" />}>
                            {record.removalHvrOrderCreatedDate != null ? (
                                <DateField value={record.removalHvrOrderCreatedDate} format="DD.MM.YYYY" />
                            ) : (
                                <TextField value={null} defaultValue="—" />
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                )}
                scroll={{ x: 1320 }}
                stretchWidth={false}
                rowKey={row => `${row?.vin}-${row?.batteryId}-${row?.installedAt}`}
            />
        </Card>
    );
};

const BmHealthEvaluationsCharts = ({ evaluations = [] }) => {
    const [t] = useTranslation();

    const [sohData, pmScoreData] = useMemo(() => {
        const soh = evaluations
            .filter(row => row.soh != null && row.sohEvaluatedAt)
            .map(row => ({
                date: row.sohEvaluatedAt,
                soh: row.soh
            }))
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

        const pmScore = evaluations
            .filter(row => row.pmScore != null && row.pmScoreEndAt)
            .map(row => ({
                date: row.pmScoreEndAt,
                pmScore: row.pmScore
            }))
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

        return [soh, pmScore];
    }, [evaluations]);

    if (!sohData.length && !pmScoreData.length) {
        return <TextField value="t_bm_no_health_evaluation" />;
    }

    return (
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div>
                <Typography.Text strong>
                    <TextField value="t_bm_soh" />
                </Typography.Text>
                {sohData.length ? (
                    <LineChart
                        chartConfig={{
                            data: sohData,
                            xField: "date",
                            yField: "soh",
                            height: 160,
                            color: stsColors.green,
                            point: { size: 3, shape: "circle" },
                            xAxis: { label: { autoRotate: true } },
                            yAxis: {
                                min: 0,
                                max: 100,
                                title: { text: "%" }
                            },
                            tooltip: { showMarkers: true }
                        }}
                    />
                ) : (
                    <TextField value={null} />
                )}
            </div>

            <div>
                <Typography.Text strong>
                    <TextField value="t_bm_pm_score" />
                </Typography.Text>
                {pmScoreData.length ? (
                    <LineChart
                        chartConfig={{
                            data: pmScoreData,
                            xField: "date",
                            yField: "pmScore",
                            meta: { pmScore: { alias: t("t_bm_pm_score") } },
                            height: 160,
                            color: stsColors.blue,
                            point: { size: 3, shape: "circle" },
                            xAxis: { label: { autoRotate: true } },
                            yAxis: {
                                title: { text: t("t_bm_pm_score") }
                            },
                            tooltip: { showMarkers: true }
                        }}
                    />
                ) : (
                    <TextField value="t_bm_pm_score_unavailable" />
                )}
            </div>
        </Space>
    );
};

const BmHealthEvaluations = ({ evaluations = [], isLoading, isError, isUpdating }) => {
    const columns = [
        {
            key: "vin",
            dataIndex: "vin",
            title: <TextField value="t_vin" />,
            render: value => <TextField value={value} />,
            width: 200
        },
        {
            key: "soh",
            dataIndex: "soh",
            title: <TextField value="t_bm_soh" />,
            render: value => <NumberField value={value} suffix="%" decimals={1} />,
            width: 130
        },
        {
            key: "sohEvaluatedAt",
            dataIndex: "sohEvaluatedAt",
            title: <TextField value="t_bm_soh_evaluated_at" />,
            render: value => <DatetimeField value={value} />,
            width: 190
        },
        {
            key: "pmScore",
            dataIndex: "pmScore",
            title: <TextField value="t_bm_pm_score" />,
            render: value => <NumberField value={value} decimals={0} />,
            width: 130
        },
        {
            key: "pmScoreEndAt",
            dataIndex: "pmScoreEndAt",
            title: <TextField value="t_bm_pm_score_end_at" />,
            render: value => <DatetimeField value={value} />,
            width: 190
        }
    ];

    return (
        <Table
            size="small"
            dataSource={evaluations}
            columns={columns}
            rowKey={row => `${row?.batteryId}-${row?.vin}-${row?.pmScoreEndAt}-${row?.sohEvaluatedAt}`}
            pagination={false}
            isLoading={isLoading}
            isError={isError}
            isUpdating={isUpdating}
            expandedRowRender={record => (
                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label={<TextField value="t_bm_warning_ladeende" />}>
                        <TextField value={record.warnings?.warning_ladeende ?? record.warning_ladeende} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<TextField value="t_bm_warning_ladevorgang" />}>
                        <TextField value={record.warnings?.warning_ladevorgang ?? record.warning_ladevorgang} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<TextField value="t_bm_warning_temp_delta" />}>
                        <TextField value={record.warnings?.warning_temp_delta ?? record.warning_temp_delta} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<TextField value="t_bm_warning_temp_range" />}>
                        <TextField value={record.warnings?.warning_temp_range ?? record.warning_temp_range} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<TextField value="t_bm_warning_temp_max" />}>
                        <TextField value={record.warnings?.warning_temp_max ?? record.warning_temp_max} />
                    </Descriptions.Item>
                </Descriptions>
            )}
            scroll={{ x: 1210 }}
            stretchWidth={false}
        />
    );
};

const BmBatteryDetail = ({ batteryId }) => {
    const healthHistoryQuery = useQuery(["bmBatteryDetail", "healthHistory", batteryId], {
        meta: {
            request: {
                method: "GET",
                url: datalayerUrls.batteries.values(),
                params: { type: "healthHistory", batteryId }
            }
        },
        enabled: Boolean(batteryId)
    });

    const assignmentsQuery = useQuery(["bmBatteryDetail", "assignments", batteryId], {
        meta: {
            request: {
                method: "GET",
                url: datalayerUrls.batteries.values(),
                params: { type: "assignments", batteryId }
            }
        },
        enabled: Boolean(batteryId)
    });

    const evaluations = healthHistoryQuery.data?.data ?? [];

    return (
        <Row gutter={[10, 10]}>
            <Col md={24} lg={8}>
                <StatusBoxWidget
                    title="t_bm_current_assignment"
                    request={{
                        url: datalayerUrls.batteries.values(),
                        params: { type: "currentAssignment", batteryId }
                    }}
                    extractData="value"
                    extractLastUpdate="timestamp"
                    renderValue={value => <TextField value={value} />}
                    icon={["fas", "truck"]}
                />
            </Col>

            <Col md={24} lg={8}>
                <StatusBoxWidget
                    title="t_bm_lifecycle_start"
                    request={{
                        url: datalayerUrls.batteries.values(),
                        params: { type: "lifecycleStart", batteryId }
                    }}
                    extractData="value"
                    icon={["fas", "calendar-day"]}
                    renderValue={value => <DateField value={value} format="DD.MM.YYYY" />}
                />
            </Col>

            <Col md={24} lg={8}>
                <StatusBoxWidget
                    title="t_bm_battery_type"
                    request={{
                        url: datalayerUrls.batteries.values(),
                        params: { type: "batteryType", batteryId }
                    }}
                    extractData="value"
                    icon={["fas", "bolt"]}
                    renderValue={value => <TextField value={value || null} />}
                />
            </Col>

            <Col md={24} lg={8}>
                <StatusBoxWidget
                    title="t_bm_soh"
                    request={{
                        url: datalayerUrls.batteries.values(),
                        params: { type: "soh", batteryId }
                    }}
                    extractData="value"
                    extractLastUpdate="timestamp"
                    icon={["fas", "battery-half"]}
                    renderValue={value => <NumberField value={value} suffix="%" decimals={1} />}
                />
            </Col>

            <Col md={24} lg={8}>
                <StatusBoxWidget
                    title="t_bm_pm_score"
                    request={{
                        url: datalayerUrls.batteries.values(),
                        params: { type: "pmScore", batteryId }
                    }}
                    extractData="value"
                    extractLastUpdate="timestamp"
                    icon={["far", "gauge-high"]}
                    renderValue={value => <NumberField value={value} decimals={0} />}
                />
            </Col>

            <Col md={24} lg={8}>
                <StatusBoxWidget
                    title="t_bm_battery_mileage"
                    request={{
                        url: datalayerUrls.batteries.values(),
                        params: { type: "batteryMileageKm", batteryId }
                    }}
                    extractData="value"
                    icon={["fas", "road"]}
                    renderValue={value => <NumberField value={value} decimals={1} suffix="km" />}
                />
            </Col>

            <Col span={24}>
                <BmVinAssignments
                    data={assignmentsQuery.data}
                    isLoading={assignmentsQuery.isLoading}
                    isError={assignmentsQuery.isError}
                    isUpdating={assignmentsQuery.isRefetching}
                />
            </Col>

            <Col span={24}>
                <Card
                    title="t_bm_health_history"
                    height={healthHistoryQuery.isLoading ? 680 : undefined}
                    isLoading={healthHistoryQuery.isLoading}
                    isError={healthHistoryQuery.isError}
                    isUpdating={healthHistoryQuery.isRefetching}
                >
                    <Space direction="vertical" size={20} style={{ width: "100%" }}>
                        <BmHealthEvaluationsCharts evaluations={evaluations} />
                        <BmHealthEvaluations
                            evaluations={evaluations}
                            isLoading={healthHistoryQuery.isLoading}
                            isError={healthHistoryQuery.isError}
                            isUpdating={healthHistoryQuery.isRefetching}
                        />
                    </Space>
                </Card>
            </Col>
        </Row>
    );
};

export default BmBatteryDetail;
